import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { DiscountRule, DiscountRuleStatus } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { useToast } from '../../context/ToastContext.js';
import { summarizeDiscountRule } from '../../lib/discountSummary.js';
import { BigButton } from '../../components/BigButton.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Page, PageHeader } from '../../components/layout.js';

const STATUS_LABELS: Record<DiscountRuleStatus, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  expired: 'Expired',
  off: 'Off',
};

const STATUS_CLASSES: Record<DiscountRuleStatus, string> = {
  active: 'bg-aqua text-teal-dark',
  scheduled: 'bg-orange text-ink',
  expired: 'bg-border text-muted',
  off: 'bg-border text-muted',
};

export function DiscountsListPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [rules, setRules] = useState<DiscountRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DiscountRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listDiscountRules();
      setRules(res.rules);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load discount rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggleActive(rule: DiscountRule) {
    setBusyId(rule.id);
    try {
      await api.updateDiscountRule(rule.id, { isActive: !rule.isActive });
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not update the rule', 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function handleMove(index: number, direction: -1 | 1) {
    const other = index + direction;
    if (other < 0 || other >= rules.length) return;
    const reordered = [...rules];
    [reordered[index], reordered[other]] = [reordered[other]!, reordered[index]!];
    try {
      await api.reorderDiscountRules({ ids: reordered.map((r) => r.id) });
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not reorder discount rules', 'error');
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteDiscountRule(pendingDelete.id);
      setPendingDelete(null);
      await load();
      showToast('Discount rule deleted', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not delete the rule', 'error');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return <Spinner label="Loading discount rules" />;
  if (error) return <EmptyState title="Could not load discount rules" description={error} />;

  return (
    <Page>
      <PageHeader
        title="Discounts"
        actions={<BigButton onClick={() => navigate('/admin/discounts/new')}>New rule</BigButton>}
      />

      <p className="field-help">
        Only the first matching rule applies to a sale - the order below decides which one wins.
      </p>

      {rules.length === 0 ? (
        <EmptyState
          title="No discount rules yet"
          description="Create one to have it apply itself automatically at the till."
          action={<BigButton onClick={() => navigate('/admin/discounts/new')}>New rule</BigButton>}
        />
      ) : (
        <ul className="list-stack">
          {rules.map((rule, index) => (
            <li key={rule.id} className="card-row flex flex-col gap-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body font-semibold text-base text-text truncate">{rule.name}</p>
                  <p className="font-body text-sm text-muted">{summarizeDiscountRule(rule)}</p>
                </div>
                <span
                  className={clsx(
                    'shrink-0 rounded-full font-heading font-bold text-sm px-3 py-1',
                    STATUS_CLASSES[rule.status],
                  )}
                >
                  {STATUS_LABELS[rule.status]}
                </span>
              </div>

              <div className="flex items-center gap-1 flex-wrap justify-end">
                <label className="flex items-center gap-2 mr-auto font-body font-semibold text-sm text-text min-h-12">
                  <input
                    type="checkbox"
                    checked={rule.isActive}
                    disabled={busyId === rule.id}
                    onChange={() => void handleToggleActive(rule)}
                    className="h-6 w-6 min-h-0 accent-teal"
                  />
                  Active
                </label>
                <button
                  type="button"
                  aria-label={`Move ${rule.name} up`}
                  disabled={index === 0}
                  onClick={() => void handleMove(index, -1)}
                  className="h-12 w-12 rounded-lg bg-aqua text-teal-dark font-heading font-bold disabled:opacity-30"
                >
                  &uarr;
                </button>
                <button
                  type="button"
                  aria-label={`Move ${rule.name} down`}
                  disabled={index === rules.length - 1}
                  onClick={() => void handleMove(index, 1)}
                  className="h-12 w-12 rounded-lg bg-aqua text-teal-dark font-heading font-bold disabled:opacity-30"
                >
                  &darr;
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/discounts/${rule.id}`)}
                  className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-teal-dark"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDelete(rule)}
                  className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-danger"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.name ?? 'this rule'}?`}
        description="Past sales keep their own record of the discount - this only stops it applying to future sales."
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={() => void handleDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </Page>
  );
}
